import axiosInstance from "utils/axiosInstance";
import { getUrl } from "utils/core";
import { APIResource } from "constants/application";

export type CustomerDocumentType =
  | "PAN_CARD_PROOF"
  | "BANK_ACCOUNT_PROOF"
  | "GST_CERTIFICATE_PROOF";

interface DocumentDownloadResponse {
  presignedUrl: string;
  expiresAt?: string;
}

interface CacheEntry {
  url: string;
  expiresAtMs: number | null;
}

const cache = new Map<string, CacheEntry>();
const inFlight = new Map<string, Promise<string>>();

function cacheKey(
  documentType: CustomerDocumentType,
  objectKey: string
): string {
  return `${documentType}:${objectKey}`;
}

function isFresh(entry: CacheEntry): boolean {
  if (!entry.expiresAtMs) {
    return true;
  }
  // Refresh 60s before expiry
  return Date.now() < entry.expiresAtMs - 60_000;
}

/**
 * Fetch a Cognito-auth presigned GET URL for a KYC document object key.
 * Caches by documentType+objectKey; pass force=true after img onError / expiry.
 *
 * Network path: POST /v1/kyc/customers/{id}/documents/download-url
 * (filter DevTools by "download-url", not "presignedUrl").
 */
export async function fetchKycDocumentPresignedUrl(options: {
  customerId: string;
  customerSource?: string;
  documentType: CustomerDocumentType;
  objectKey: string;
  force?: boolean;
}): Promise<string> {
  const {
    customerId,
    customerSource = localStorage.getItem("appname") || "BRP",
    documentType,
    objectKey: rawObjectKey,
    force = false,
  } = options;

  const objectKey = normalizeObjectKey(rawObjectKey);
  if (!objectKey) {
    throw new Error("objectKey is required");
  }

  const key = cacheKey(documentType, objectKey);
  if (!force) {
    const cached = cache.get(key);
    if (cached && isFresh(cached)) {
      return cached.url;
    }
    const pending = inFlight.get(key);
    if (pending) {
      return pending;
    }
  }

  const request = (async () => {
    const url = getUrl(
      APIResource.documentDownloadUrl,
      [{ variableName: "customerId", variableValue: customerId }],
      [{ paramName: "customerSource", paramValue: customerSource }]
    );
    const response = await axiosInstance.post<DocumentDownloadResponse>(url, {
      documentType,
      objectKey,
    });
    const body = response.data as DocumentDownloadResponse & {
      data?: DocumentDownloadResponse;
    };
    const presignedUrl = body?.presignedUrl || body?.data?.presignedUrl;
    if (!presignedUrl) {
      throw new Error("Presigned URL missing from download-url response");
    }
    const expiresAt = body?.expiresAt || body?.data?.expiresAt;
    const expiresAtMs = expiresAt ? Date.parse(String(expiresAt)) : null;
    cache.set(key, {
      url: presignedUrl,
      expiresAtMs: Number.isFinite(expiresAtMs) ? expiresAtMs : null,
    });
    return presignedUrl;
  })();

  inFlight.set(key, request);
  try {
    return await request;
  } finally {
    inFlight.delete(key);
  }
}

/** Strip CDN/S3 URL down to object key when older records stored a full public URL. */
export function normalizeObjectKey(value?: string | null): string {
  if (!value) return "";
  const trimmed = value.trim();
  if (!trimmed) return "";
  if (trimmed.startsWith("http://") || trimmed.startsWith("https://")) {
    try {
      const path = new URL(trimmed).pathname.replace(/^\/+/, "");
      return path || trimmed;
    } catch {
      return trimmed;
    }
  }
  return trimmed;
}

export function isUsableDocumentDisplayUrl(
  src: string | null | undefined
): boolean {
  if (!src) return false;
  return (
    src.startsWith("blob:") ||
    src.startsWith("data:") ||
    src.startsWith("http://") ||
    src.startsWith("https://")
  );
}

export async function resolveKycDocumentDisplayUrl(options: {
  customerId: string;
  customerSource?: string;
  documentType: CustomerDocumentType;
  objectKey?: string | null;
  force?: boolean;
}): Promise<string | null> {
  if (!options.objectKey) {
    return null;
  }
  try {
    return await fetchKycDocumentPresignedUrl({
      ...options,
      objectKey: options.objectKey,
    });
  } catch (err) {
    console.error("Failed to resolve KYC document display URL", err);
    return null;
  }
}

/** True when src is a remote URL that may need re-presign (not local blob/data). */
export function isRemoteDocumentDisplayUrl(src: string | null | undefined): boolean {
  if (!src) return false;
  return src.startsWith("http://") || src.startsWith("https://");
}
